import React, { useState, useEffect } from 'react';
import { updateEvent, deleteEvent, getSchoolCalendars, getFamilyMembers, getItineraries, createItinerary, SchoolCalendar } from '../services';
import { FamilyMember, EventType, EventData, ItineraryListItem } from '../types';
import './EventEditModal.css';

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: 'appointment', label: 'Appointment' },
  { value: 'activity', label: 'Activity' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'travel', label: 'Travel' },
  { value: 'meal', label: 'Meal' },
  { value: 'block', label: 'Time Block' },
  { value: 'other', label: 'Other' },
];

interface EventEditModalProps {
  event: EventData;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  initialFocus?: 'time' | 'location';
}

const EventEditModal: React.FC<EventEditModalProps> = ({ event, onClose, onSave, onDelete, initialFocus }) => {
  const [title, setTitle] = useState(event.title);
  const [startDate, setStartDate] = useState(() => {
    // Use local date, not UTC date
    const d = new Date(event.start_time);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(event.end_time);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [startTime, setStartTime] = useState(() => {
    return new Date(event.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  });
  const [endTime, setEndTime] = useState(() => {
    return new Date(event.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  });
  const timeRef = React.useRef<HTMLInputElement>(null);
  const locationRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (initialFocus === 'time' && timeRef.current) {
      setTimeout(() => timeRef.current?.focus(), 100);
    } else if (initialFocus === 'location' && locationRef.current) {
      setTimeout(() => { locationRef.current?.focus(); locationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    }
  }, [initialFocus]);

  const [location, setLocation] = useState(event.location || '');
  const [address, setAddress] = useState(event.address || '');
  const [contextNotes, setContextNotes] = useState(event.context_notes || '');
  const [isRecurring, setIsRecurring] = useState(event.is_recurring || false);
  const [selectedDays, setSelectedDays] = useState<string[]>(() => {
    // Parse existing recurrence rule to get days
    if (event.recurrence_rule) {
      const match = event.recurrence_rule.match(/BYDAY=([A-Z,]+)/);
      if (match) {
        return match[1].split(',');
      }
    }
    return [];
  });
  const [schoolCalendarId, setSchoolCalendarId] = useState<string>(event.school_calendar_data || '');
  const [schoolCalendars, setSchoolCalendars] = useState<SchoolCalendar[]>([]);
  const [eventType, setEventType] = useState<EventType>(event.event_type || 'other');
  const [guardianId, setGuardianId] = useState<string>(event.guardian_id?.toString() || '');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  // API returns 'itinerary' on update, but 'itinerary_id' on list - check both
  const [itineraryId, setItineraryId] = useState<string>(
    (event.itinerary_id ?? event.itinerary)?.toString() || ''
  );
  const [itineraries, setItineraries] = useState<ItineraryListItem[]>([]);
  const [showNewItinerary, setShowNewItinerary] = useState(false);
  const [newItineraryTitle, setNewItineraryTitle] = useState('');
  const [newItineraryStartDate, setNewItineraryStartDate] = useState(startDate);
  const [newItineraryEndDate, setNewItineraryEndDate] = useState(endDate);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load AI school calendars for recurring events
    const loadSchoolCalendars = async () => {
      try {
        const response = await getSchoolCalendars();
        setSchoolCalendars(response.calendars);
      } catch (err) {
        console.error('Failed to load school calendars:', err);
      }
    };
    // Load family members for guardian dropdown
    const loadFamilyMembers = async () => {
      try {
        const response = await getFamilyMembers();
        setFamilyMembers(response.members);
      } catch (err) {
        console.error('Failed to load family members:', err);
      }
    };
    // Load itineraries for grouping events
    const loadItineraries = async () => {
      try {
        const data = await getItineraries();
        setItineraries(data);
      } catch (err) {
        console.error('Failed to load itineraries:', err);
      }
    };
    loadSchoolCalendars();
    loadFamilyMembers();
    loadItineraries();
  }, []);

  const DAYS_OF_WEEK = [
    { key: 'MO', label: 'Mon' },
    { key: 'TU', label: 'Tue' },
    { key: 'WE', label: 'Wed' },
    { key: 'TH', label: 'Thu' },
    { key: 'FR', label: 'Fri' },
    { key: 'SA', label: 'Sat' },
    { key: 'SU', label: 'Sun' },
  ];

  const toggleDay = (day: string) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const isEditable = event.source === 'vizday';

  const handleItineraryChange = (value: string) => {
    if (value === 'new') {
      setShowNewItinerary(true);
      setItineraryId('');
    } else {
      setShowNewItinerary(false);
      setItineraryId(value);
    }
  };

  const handleCreateItinerary = async () => {
    if (!newItineraryTitle.trim()) {
      setError('Please enter a title for the trip');
      return;
    }
    try {
      const newItinerary = await createItinerary({
        title: newItineraryTitle.trim(),
        start_date: newItineraryStartDate,
        end_date: newItineraryEndDate,
        type: 'trip',
      });
      setItineraries((prev) => [
        ...prev,
        {
          id: newItinerary.id,
          title: newItinerary.title,
          type: newItinerary.type,
          is_published: newItinerary.is_published ?? false,
          published_at: newItinerary.published_at ?? null,
          start_date: newItinerary.start_date,
          end_date: newItinerary.end_date,
          family_name: newItinerary.family_name,
          item_count: newItinerary.items?.length ?? 0,
          created_at: newItinerary.created_at,
          updated_at: newItinerary.updated_at ?? newItinerary.created_at,
        },
      ]);
      setItineraryId(newItinerary.id.toString());
      setShowNewItinerary(false);
      setNewItineraryTitle('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create itinerary');
    }
  };

  const handleSave = async () => {
    if (!isEditable) return;

    setIsSaving(true);
    setError('');

    try {
      // Combine date and time
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const endDateTime = new Date(`${endDate}T${endTime}`);

      // Build recurrence rule if recurring
      let recurrence_rule = '';
      if (isRecurring && selectedDays.length > 0) {
        recurrence_rule = `FREQ=WEEKLY;BYDAY=${selectedDays.join(',')}`;
      }

      await updateEvent(event.id, {
        title,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        location,
        address,
        context_notes: contextNotes,
        event_type: eventType,
        guardian_id: guardianId ? parseInt(guardianId) : null,
        itinerary: itineraryId ? parseInt(itineraryId) : null,
        is_recurring: isRecurring && selectedDays.length > 0,
        recurrence_rule,
        school_calendar_data: isRecurring && schoolCalendarId ? schoolCalendarId : null,
      });

      onSave();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditable) return;
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    setIsDeleting(true);
    setError('');

    try {
      await deleteEvent(event.id);
      if (onDelete) onDelete();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDisplayTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDisplayDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const isSameDay = (date1: string, date2: string) => {
    return new Date(date1).toDateString() === new Date(date2).toDateString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditable ? 'Edit Event' : 'Event Details'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-body">
          {isEditable ? (
            <>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startDate">Start Date</label>
                  <input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="startTime">Start Time</label>
                  <input
                    id="startTime"
                    type="time"
                    ref={timeRef}
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="endDate">End Date</label>
                  <input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endTime">End Time</label>
                  <input
                    id="endTime"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                  />
                  Repeats weekly
                </label>
              </div>

              {isRecurring && (
                <>
                  <div className="form-group">
                    <label>Days of week</label>
                    <div className="days-selector">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.key}
                          type="button"
                          className={`day-btn ${selectedDays.includes(day.key) ? 'selected' : ''}`}
                          onClick={() => toggleDay(day.key)}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {schoolCalendars.length > 0 && (
                    <div className="form-group">
                      <label htmlFor="schoolCalendar">Only on school days (optional)</label>
                      <select
                        id="schoolCalendar"
                        value={schoolCalendarId}
                        onChange={(e) => setSchoolCalendarId(e.target.value)}
                        className="form-input"
                      >
                        <option value="">-- Show every week --</option>
                        {schoolCalendars.map(cal => (
                          <option key={cal.id} value={cal.id}>
                            {cal.school_name}
                          </option>
                        ))}
                      </select>
                      <span className="form-hint">
                        Skip days when school is not in session (breaks, holidays)
                      </span>
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  id="location"
                  type="text"
                  ref={locationRef}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Soccer Field"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g., 123 Main St, City, State"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contextNotes">Notes for briefing</label>
                <textarea
                  id="contextNotes"
                  value={contextNotes}
                  onChange={(e) => setContextNotes(e.target.value)}
                  placeholder="e.g., Bring umbrella&#10;Gift is wrapped&#10;She prefers window seat"
                  className="form-input form-textarea"
                  rows={3}
                />
                <span className="form-hint">
                  One note per line. These appear in your daily briefing.
                </span>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventType">Event Type</label>
                  <select
                    id="eventType"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value as EventType)}
                    className="form-input"
                  >
                    {EVENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {familyMembers.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="guardian">Guardian</label>
                    <select
                      id="guardian"
                      value={guardianId}
                      onChange={(e) => setGuardianId(e.target.value)}
                      className="form-input"
                    >
                      <option value="">-- No guardian assigned --</option>
                      {familyMembers
                        .filter(m => m.role === 'adult' || m.role === 'caregiver' || m.role === 'relative')
                        .map(member => (
                          <option key={member.id} value={member.id}>
                            {member.name}
                          </option>
                        ))}
                    </select>
                    <span className="form-hint">
                      Adult responsible for supervising this event
                    </span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="itinerary">Trip / Itinerary</label>
                <select
                  id="itinerary"
                  value={showNewItinerary ? 'new' : itineraryId}
                  onChange={(e) => handleItineraryChange(e.target.value)}
                  className="form-input"
                >
                  <option value="">-- Not part of a trip --</option>
                  {itineraries.map(itin => (
                    <option key={itin.id} value={itin.id}>
                      {itin.title} ({itin.start_date} - {itin.end_date})
                    </option>
                  ))}
                  <option value="new">+ Create new trip...</option>
                </select>
                <span className="form-hint">
                  Group related events to avoid false conflict alerts
                </span>
              </div>

              {showNewItinerary && (
                <div className="new-itinerary-form">
                  <div className="form-group">
                    <label htmlFor="newItineraryTitle">Trip Name</label>
                    <input
                      id="newItineraryTitle"
                      type="text"
                      value={newItineraryTitle}
                      onChange={(e) => setNewItineraryTitle(e.target.value)}
                      placeholder="e.g., Disney Trip, Beach Vacation"
                      className="form-input"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="newItinStart">Start Date</label>
                      <input
                        id="newItinStart"
                        type="date"
                        value={newItineraryStartDate}
                        onChange={(e) => setNewItineraryStartDate(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="newItinEnd">End Date</label>
                      <input
                        id="newItinEnd"
                        type="date"
                        value={newItineraryEndDate}
                        onChange={(e) => setNewItineraryEndDate(e.target.value)}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-create-itinerary"
                    onClick={handleCreateItinerary}
                  >
                    Create Trip
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="event-view">
              <div className="view-row">
                <span className="view-label">Title</span>
                <span className="view-value">{event.title}</span>
              </div>
              <div className="view-row">
                <span className="view-label">Time</span>
                <span className="view-value">
                  {isSameDay(event.start_time, event.end_time)
                    ? `${formatDisplayTime(event.start_time)} - ${formatDisplayTime(event.end_time)}`
                    : `${formatDisplayDateTime(event.start_time)} - ${formatDisplayDateTime(event.end_time)}`
                  }
                </span>
              </div>
              {event.location && (
                <div className="view-row">
                  <span className="view-label">Location</span>
                  <span className="view-value">{event.location}</span>
                </div>
              )}
              {event.calendar_name && (
                <div className="view-row">
                  <span className="view-label">Calendar</span>
                  <span className="view-value">{event.calendar_name}</span>
                </div>
              )}
              <div className="view-note">
                This event is from an external calendar and cannot be edited here.
                Edit it in the source calendar and sync to update.
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {isEditable ? (
            <>
              <button
                className="btn-delete"
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <div className="footer-right">
                <button className="btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={handleSave}
                  disabled={isSaving || isDeleting}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : (
            <button className="btn-cancel" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventEditModal;
